import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { compressImage } from '../../utils/compressImage';

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const IcoMenu = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
  </svg>
);
const IcoBranch = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IcoEdit = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IcoCopy = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);
const IcoQR = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    <rect x="5" y="5" width="3" height="3" fill="currentColor" stroke="none"/>
    <rect x="16" y="5" width="3" height="3" fill="currentColor" stroke="none"/>
    <rect x="5" y="16" width="3" height="3" fill="currentColor" stroke="none"/>
    <line x1="14" y1="14" x2="14" y2="14"/><line x1="17" y1="14" x2="21" y2="14"/><line x1="14" y1="17" x2="14" y2="21"/><line x1="21" y1="17" x2="17" y2="17"/><line x1="17" y1="17" x2="21" y2="21"/>
  </svg>
);
const IcoTrash = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

// ── Brand Card ────────────────────────────────────────────────────────────────
function BrandCard({ r, bCount, hasMenu, isAdmin, onEdit, onDuplicate, onDelete, onMenu, onSlides, onBranches, onQR }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderTop: `1.5px solid ${hovered ? '#90CAF9' : '#EBEBEB'}`,
        borderRight: `1.5px solid ${hovered ? '#90CAF9' : '#EBEBEB'}`,
        borderBottom: `1.5px solid ${hovered ? '#90CAF9' : '#EBEBEB'}`,
        borderLeft: '5px solid #42A5F5',
        borderRadius: 16,
        boxShadow: hovered ? '0 6px 24px rgba(0,0,0,0.10)' : '0 1px 6px rgba(0,0,0,0.06)',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ── */}
      <div style={{ padding: '11px 14px 10px', display: 'flex', gap: 12, alignItems: 'center', position: 'relative' }}>
        {/* Admin owner badge — top-right */}
        {isAdmin && r.owner_username && (
          <div style={{
            position: 'absolute', top: 8, right: 10,
            background: '#F0F4FF', border: '1px solid #C7D7FD',
            borderRadius: 20, padding: '1px 8px',
            fontSize: 10, fontWeight: 600, color: '#3B5BDB',
            maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            👤 {r.owner_username}
          </div>
        )}

        {/* Branches badge — top-right for non-admin */}
        {!isAdmin && (
          <button
            onClick={e => { e.stopPropagation(); onBranches(e); }}
            title="View Branches"
            style={{
              position: 'absolute', top: 8, right: 10,
              display: 'flex', alignItems: 'center', gap: 4,
              background: '#F0FFF4', border: '1px solid #86EFAC',
              borderRadius: 20, padding: '2px 9px',
              fontSize: 11, fontWeight: 700, color: '#15803D',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#DCFCE7'; e.currentTarget.style.borderColor = '#4ADE80'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#F0FFF4'; e.currentTarget.style.borderColor = '#86EFAC'; }}
          >
            📍 {bCount} {bCount === 1 ? 'Branch' : 'Branches'}
          </button>
        )}

        {/* Logo / Avatar */}
        {r.logo_path ? (
          <img
            src={r.logo_path} alt={r.name}
            style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', background: '#F7F7F7', border: '1.5px solid #EBEBEB', padding: 2, flexShrink: 0 }}
          />
        ) : (
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg,#E3F2FD,#BBDEFB)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#1565C0', fontWeight: 800, fontSize: 19,
            boxShadow: '0 2px 8px rgba(33,150,243,0.15)',
          }}>
            {r.name?.charAt(0).toUpperCase()}
          </div>
        )}

        {/* Name + Arabic */}
        <div style={{ flex: 1, minWidth: 0, marginTop: (isAdmin && r.owner_username) || !isAdmin ? 14 : 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.name}
          </div>
          {r.name_ar && (
            <div style={{ fontFamily: 'Cairo', fontSize: 12, color: '#999', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.name_ar}
            </div>
          )}
        </div>
      </div>

      {/* ── Action row: Menu Edit | QR Code | Slide Builder ── */}
      <div style={{ display: 'flex', borderTop: '1px solid #F5F5F5', borderBottom: '1px solid #F5F5F5' }}>
        {/* Menu Edit */}
        <button
          onClick={e => { e.stopPropagation(); onMenu(e); }}
          title={hasMenu ? 'Open Menu Editor (menu exists)' : 'Open Menu Editor'}
          style={{
            flex: 1, padding: '7px 8px', border: 'none', background: '#FEFCE8',
            cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
            borderRight: '1px solid #FEF08A', transition: 'background 0.15s', position: 'relative',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#FEF9C3'}
          onMouseOut={e => e.currentTarget.style.background = '#FEFCE8'}
        >
          <div
            className={hasMenu ? 'pm-menu-pulse' : ''}
            style={{ color: hasMenu ? '#DC2626' : '#2563EB', display: 'flex' }}
          >
            <IcoMenu />
          </div>
          <span style={{
            fontSize: 10,
            color: hasMenu ? '#DC2626' : '#888',
            fontWeight: hasMenu ? 700 : 600,
            letterSpacing: 0.5,
          }}>
            MENU EDIT
          </span>
          {hasMenu && (
            <span
              title="This brand has a menu"
              style={{
                position: 'absolute', top: 4, right: 6,
                width: 8, height: 8, borderRadius: '50%',
                background: '#DC2626', boxShadow: '0 0 0 2px #FEFCE8',
                animation: 'pmMenuDot 1.2s ease-in-out infinite',
              }}
            />
          )}
        </button>

        {/* Branches (admin) | QR Code (non-admin) */}
        {isAdmin ? (
          <button
            onClick={e => { e.stopPropagation(); onBranches(e); }}
            title="Branches"
            style={{
              flex: 1, padding: '7px 8px', border: 'none', background: '#F0FDF4',
              cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
              borderRight: '1px solid #BBF7D0', transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#DCFCE7'}
            onMouseOut={e => e.currentTarget.style.background = '#F0FDF4'}
          >
            <div style={{ color: '#15803D', display: 'flex' }}><IcoBranch /></div>
            <span style={{ fontSize: 10, color: '#888', fontWeight: 600, letterSpacing: 0.5 }}>
              BRANCHES {bCount > 0 ? `(${bCount})` : ''}
            </span>
          </button>
        ) : (
          <button
            onClick={e => { e.stopPropagation(); onQR(e); }}
            title="QR Code"
            style={{
              flex: 1, padding: '7px 8px', border: 'none', background: '#F0FFF4',
              cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
              borderRight: '1px solid #BBF7D0', transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#DCFCE7'}
            onMouseOut={e => e.currentTarget.style.background = '#F0FFF4'}
          >
            <div style={{ color: '#16A34A', display: 'flex' }}><IcoQR /></div>
            <span style={{ fontSize: 10, color: '#888', fontWeight: 600, letterSpacing: 0.5 }}>QR CODE</span>
          </button>
        )}

        {/* Slide Builder */}
        <button
          onClick={e => { e.stopPropagation(); onSlides(e); }}
          title="Slide Builder"
          style={{
            flex: 1, padding: '7px 8px', border: 'none', background: '#EFF6FF',
            cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'background 0.15s',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#DBEAFE'}
          onMouseOut={e => e.currentTarget.style.background = '#EFF6FF'}
        >
          <span style={{ fontSize: 18 }}>🎨</span>
          <span style={{ fontSize: 10, color: '#888', fontWeight: 600, letterSpacing: 0.5 }}>SLIDES</span>
        </button>
      </div>

      {/* ── Actions: Edit | Duplicate | Delete — admin only ── */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 10px' }}>
          <button
            onClick={onEdit}
            title="Edit Brand"
            style={{
              flex: 1, height: 30, borderRadius: 8, border: '1.5px solid #E0E0E0',
              background: '#FAFAFA', color: '#444', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#E3F2FD'; e.currentTarget.style.borderColor = '#90CAF9'; e.currentTarget.style.color = '#1565C0'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#FAFAFA'; e.currentTarget.style.borderColor = '#E0E0E0'; e.currentTarget.style.color = '#444'; }}
          >
            <IcoEdit />
          </button>
          <button
            onClick={onDuplicate}
            title="Duplicate Brand"
            style={{
              flex: 1, height: 30, borderRadius: 8, border: '1.5px solid #E0E0E0',
              background: '#FAFAFA', color: '#444', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#E8F5E9'; e.currentTarget.style.borderColor = '#A5D6A7'; e.currentTarget.style.color = '#2E7D32'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#FAFAFA'; e.currentTarget.style.borderColor = '#E0E0E0'; e.currentTarget.style.color = '#444'; }}
          >
            <IcoCopy />
          </button>
          <button
            onClick={onDelete}
            title="Delete Brand"
            style={{
              flex: 1, height: 30, borderRadius: 8, border: '1.5px solid #FFCDD2',
              background: '#FFF5F5', color: '#C62828', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#FFEBEE'; e.currentTarget.style.borderColor = '#EF9A9A'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#FFF5F5'; e.currentTarget.style.borderColor = '#FFCDD2'; }}
          >
            <IcoTrash />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Restaurants() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Filter source: react-router state (when navigated from menu-app/Users)
  // OR sessionStorage (when handed off from main-app AdminPanel — the
  // outer App.js stashes it there because it can't pass router state into
  // a sub-app's MemoryRouter).
  const stashRaw = (() => { try { return JSON.parse(sessionStorage.getItem('pm_menu_filter_user') || 'null'); } catch { return null; } })();
  const filterUser = location.state?.filterUser || stashRaw?.userId || null;
  const filterUsername = location.state?.filterUsername || stashRaw?.username || null;
  const autoOpenAdd = location.state?.openAdd || false;
  const autoOpenAddBranch = location.state?.openAddBranch || false;
  // Clear the stash after first use so navigating back to /brands later
  // (without going through AdminPanel) doesn't keep filtering.
  useEffect(() => {
    if (stashRaw) sessionStorage.removeItem('pm_menu_filter_user');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [restaurants, setRestaurants] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', name_ar: '', font: 'Cairo', owner_id: '', logo_path: '', capabilities: [] });
  const [logoUploading, setLogoUploading] = useState(false);
  const [branchCountMap, setBranchCountMap] = useState({});
  const [addBranchHint, setAddBranchHint] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [onlyWithMenu, setOnlyWithMenu] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // View mode (table | cards) — persisted in localStorage.
  // Stale values fall back to 'table'.
  const [viewMode, setViewModeRaw] = useState(() => {
    try {
      const v = localStorage.getItem('brands_view');
      return v === 'cards' ? 'cards' : 'table';
    } catch { return 'table'; }
  });
  const setViewMode = (v) => {
    setViewModeRaw(v);
    try { localStorage.setItem('brands_view', v); } catch {}
  };

  // Row kebab menu — same single-portal design used on AdminPanel users.
  const [openRowMenuId, setOpenRowMenuId] = useState(null);
  const [openRowMenuPos, setOpenRowMenuPos] = useState(null);
  const closeRowMenu = () => { setOpenRowMenuId(null); setOpenRowMenuPos(null); };
  useEffect(() => {
    function onMouseDown(e) {
      if (!openRowMenuId) return;
      if (e.target.closest('.brn-row-menu')) return;
      if (e.target.closest('.brn-row-kebab')) return;
      if (e.target.closest('.brn-row')) return; // row click reassigns; don't pre-close
      closeRowMenu();
    }
    function onScroll() { if (openRowMenuId) closeRowMenu(); }
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [openRowMenuId]);

  React.useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const load = async () => {
    try {
      const rRes = await api.get('/brands');
      const sorted = [...rRes.data].sort((a, b) => {
        const ownerCmp = (a.owner_username || '').toLowerCase().localeCompare((b.owner_username || '').toLowerCase());
        if (ownerCmp !== 0) return ownerCmp;
        return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
      });
      setRestaurants(sorted);
      if (user?.role === 'admin') {
        const uRes = await api.get('/users');
        setOwners(uRes.data);
      }
    } catch {}
    setLoading(false);
  };

  const loadBranches = async () => {
    try {
      const res = await api.get('/branches');
      const counts = {};
      res.data.forEach(b => { counts[b.brand_id] = (counts[b.brand_id] || 0) + 1; });
      setBranchCountMap(counts);
    } catch {}
  };

  useEffect(() => { load(); loadBranches(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Staff (brand_manager / branch_manager / operator) shouldn't land on the
  // Brands page — there's nothing for them to do here. Auto-route them to
  // their first brand's Menu Editor (which is the most common destination).
  useEffect(() => {
    if (loading) return;
    const role = user?.role;
    if (!['brand_manager','branch_manager','operator'].includes(role)) return;
    if (restaurants.length === 0) return;
    const first = restaurants[0];
    navigate(`/brands/${first.id}/menu`, { replace: true });
  }, [loading, restaurants, user, navigate]);

  // Auto-open add modal or branch hint from user card shortcuts
  useEffect(() => {
    if (autoOpenAdd && !loading) {
      setEditing(null);
      setForm({ name: '', name_ar: '', font: 'Cairo', owner_id: filterUser || '', logo_path: '', capabilities: [] });
      setShowModal(true);
    }
    if (autoOpenAddBranch && !loading) {
      setAddBranchHint(true);
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [pendingLogoPreview, setPendingLogoPreview] = useState('');
  const [assignedManagers, setAssignedManagers] = useState([]);

  // Pull the manager assignments for the brand currently open in the modal.
  useEffect(() => {
    if (!editing || !showModal) { setAssignedManagers([]); return; }
    let cancelled = false;
    api.get(`/brands/${editing.id}/managers`)
      .then(res => { if (!cancelled) setAssignedManagers(res.data); })
      .catch(() => { if (!cancelled) setAssignedManagers([]); });
    return () => { cancelled = true; };
  }, [editing, showModal]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', name_ar: '', font: 'Cairo', owner_id: '', logo_path: '', capabilities: [] });
    setPendingLogoFile(null);
    setPendingLogoPreview('');
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      name_ar: r.name_ar || '',
      font: r.font || 'Cairo',
      owner_id: r.user_id,
      logo_path: r.logo_path || '',
      // Reflect actual capabilities — including empty arrays. Admin can now
      // freely toggle signage off too.
      capabilities: Array.isArray(r.capabilities) ? [...r.capabilities] : [],
    });
    setPendingLogoFile(null);
    setPendingLogoPreview('');
    setShowModal(true);
  };

  // Logo selection: stash file locally, defer the actual upload until brand exists.
  // For NEW brands we don't have a brand_id yet, and the backend now requires one.
  // For EDIT mode we COULD upload immediately, but doing it on submit keeps the UX
  // consistent and atomic — picking a logo without saving the form leaves no orphan.
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file, 600, 600, 0.88);
      setPendingLogoFile(compressed);
      setPendingLogoPreview(URL.createObjectURL(compressed));
    } catch { toast.error('Could not read image'); }
  };

  const uploadLogoForBrand = async (brandId) => {
    if (!pendingLogoFile) return null;
    const fd = new FormData();
    fd.append('logo', pendingLogoFile);
    fd.append('brand_id', brandId);
    const res = await api.post('/upload/logo', fd);
    // Use displayUrl (with ?v=<ts>) so browsers don't serve a cached prior logo at the same path.
    return res.data.displayUrl || res.data.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLogoUploading(true);
    try {
      let brandId, finalForm = { ...form };
      if (editing) {
        // Edit: upload logo first (we already have the brand id), then update
        if (pendingLogoFile) {
          const newLogo = await uploadLogoForBrand(editing.id);
          if (newLogo) finalForm.logo_path = newLogo;
        }
        await api.put(`/brands/${editing.id}`, finalForm);
        brandId = editing.id;
      } else {
        // Create: brand first to get an id, then upload logo against that id, then PUT logo_path
        const created = await api.post('/brands', finalForm);
        brandId = created.data.id;
        if (pendingLogoFile) {
          const newLogo = await uploadLogoForBrand(brandId);
          if (newLogo) await api.put(`/brands/${brandId}`, { ...finalForm, logo_path: newLogo });
        }
      }
      toast.success(editing ? 'Updated!' : 'Brand created!');
      setShowModal(false);
      setPendingLogoFile(null);
      setPendingLogoPreview('');
      await load(); await loadBranches();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setLogoUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this brand and all its data?')) return;
    await api.delete(`/brands/${id}`);
    toast.success('Deleted');
    load(); loadBranches();
  };

  const handleDuplicate = async (id) => {
    try {
      await api.post(`/brands/${id}/duplicate`);
      toast.success('Duplicated!');
      await load(); await loadBranches();
    } catch { toast.error('Duplicate failed'); }
  };

  if (loading) return <div className="spinner" style={{ marginTop: 60 }} />;

  const hasMenuFn = (r) => Number(r.category_count || 0) > 0;
  const baseList = filterUser
    ? restaurants.filter(r => r.user_id === filterUser)
    : restaurants;
  const withMenuTotal = baseList.filter(hasMenuFn).length;
  const displayed = baseList
    .filter(r => !onlyWithMenu || hasMenuFn(r))
    .filter(r => !searchText || r.name.toLowerCase().includes(searchText.toLowerCase()) || (r.name_ar || '').includes(searchText));

  return (
    <div className="page">

      {/* Filter banner */}
      {filterUser && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '10px 16px', marginBottom: 18 }}>
          <span style={{ fontSize: 18 }}>👤</span>
          <span style={{ flex: 1, fontSize: 14, color: '#1D4ED8', fontWeight: 500 }}>
            Showing brands for <strong>{filterUsername}</strong>
          </span>
          <button
            onClick={() => navigate('/', { state: {} })}
            style={{ background: 'none', border: '1.5px solid #BFDBFE', borderRadius: 8, padding: '4px 12px', color: '#1D4ED8', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >
            ✕ Clear filter
          </button>
        </div>
      )}

      {/* Add Branch hint banner */}
      {addBranchHint && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '10px 16px', marginBottom: 18 }}>
          <span style={{ fontSize: 18 }}>📍</span>
          <span style={{ flex: 1, fontSize: 14, color: '#15803D', fontWeight: 500 }}>
            Click a brand below to open its branches, then add a new branch from there.
          </span>
          <button
            onClick={() => setAddBranchHint(false)}
            style={{ background: 'none', border: '1.5px solid #BBF7D0', borderRadius: 8, padding: '4px 12px', color: '#15803D', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >✕</button>
        </div>
      )}

      {/* Pulse keyframes for "has menu" indicator */}
      <style>{`
        @keyframes pmMenuDot {
          0%, 100% { opacity: 1;   transform: scale(1); }
          50%      { opacity: 0.35; transform: scale(0.7); }
        }
        .pm-menu-pulse { animation: pmMenuPulse 1.2s ease-in-out infinite; }
        @keyframes pmMenuPulse {
          0%, 100% { opacity: 1;   filter: drop-shadow(0 0 0 rgba(220,38,38,0.0)); }
          50%      { opacity: 0.55; filter: drop-shadow(0 0 4px rgba(220,38,38,0.6)); }
        }
        @keyframes pmChipPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.45); }
          50%      { box-shadow: 0 0 0 6px rgba(220,38,38,0.0); }
        }
      `}</style>

      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 className="page-title" style={{ margin: 0 }}>
            Brands
            {displayed.length > 0 && (
              <span style={{ color: '#E53935', fontWeight: 700, fontSize: 20, marginLeft: 10 }}>
                ({displayed.length})
              </span>
            )}
          </h1>

          {/* "Brands with menus" counter — clickable to toggle filter */}
          <button
            type="button"
            onClick={() => setOnlyWithMenu(v => !v)}
            title={onlyWithMenu ? 'Click to show all brands' : 'Click to show only brands with menus'}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              height: 32, padding: '0 12px', borderRadius: 16,
              border: `1.5px solid ${onlyWithMenu ? '#DC2626' : '#FCA5A5'}`,
              background: onlyWithMenu ? '#DC2626' : '#FEF2F2',
              color: onlyWithMenu ? '#FFF' : '#B91C1C',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.15s',
              animation: onlyWithMenu ? 'none' : 'pmChipPulse 1.6s ease-in-out infinite',
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: onlyWithMenu ? '#FFF' : '#DC2626',
              animation: 'pmMenuDot 1.2s ease-in-out infinite',
            }}/>
            With Menu ({withMenuTotal})
            {onlyWithMenu && <span style={{ marginLeft: 4, fontWeight: 800 }}>✕</span>}
          </button>

          <input
            type="text"
            placeholder="Search brands…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{
              height: 36, padding: '0 12px', borderRadius: 10, border: '1.5px solid #E0E0E0',
              fontSize: 13, outline: 'none', minWidth: 180, background: '#FAFAFA',
            }}
          />

          {/* View-mode toggle (Table | Cards) — mirrors the AdminPanel users layout */}
          <div style={{
            display: 'inline-flex', background: '#F1F5F9',
            border: '1px solid #CBD5E1', borderRadius: 8, padding: 2,
          }}>
            {[
              { key: 'table', label: 'Table',
                icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 6h18M3 12h18M3 18h18"/></svg> },
              { key: 'cards', label: 'Cards',
                icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="3"  width="8" height="8" rx="1"/><rect x="13" y="3"  width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg> },
            ].map(v => {
              const active = viewMode === v.key;
              return (
                <button key={v.key} type="button" onClick={() => setViewMode(v.key)}
                  title={`${v.label} view`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    border: 'none',
                    background: active ? '#fff' : 'transparent',
                    color:      active ? '#1D4ED8' : '#475569',
                    boxShadow:  active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  }}>
                  {v.icon}<span>{v.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        {user?.role === 'admin' && <button className="btn btn-primary" onClick={openAdd}>+ Add</button>}
      </div>

      {/* Empty state */}
      {displayed.length === 0 && (
        <div className="empty-state card">
          <div style={{ fontSize: 48, marginBottom: 12 }}>🍽</div>
          <h3>{filterUser ? `No brands for ${filterUsername}` : 'No brands yet'}</h3>
          <p>{filterUser ? `Create the first brand for ${filterUsername}` : 'Add your first restaurant to get started'}</p>
          {user?.role === 'admin' && <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openAdd}>+ Add Brand</button>}
        </div>
      )}

      {/* Render the chosen view */}
      {(() => {
        const isAdmin = user?.role === 'admin';

        // ── Kebab menu open helper ─────────────────────────────────
        const MENU_W = 220;
        const MENU_H_EST = 320;
        const openMenuAt = (brand, x, y) => {
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          let top  = y + 4;
          let left = x;
          if (top + MENU_H_EST > vh - 8) top = Math.max(8, y - MENU_H_EST - 4);
          if (left < 8) left = 8;
          if (left + MENU_W > vw - 8) left = vw - MENU_W - 8;
          setOpenRowMenuId(brand.id);
          setOpenRowMenuPos({ top, left });
        };
        const renderKebabButton = (brand) => (
          <button
            type="button"
            className="brn-row-kebab"
            title="Row actions"
            onClick={(e) => { e.stopPropagation(); openMenuAt(brand, e.clientX, e.clientY); }}
            style={{
              width: 28, height: 28, borderRadius: 6,
              border: '1px solid transparent',
              background: openRowMenuId === brand.id ? '#EEF2FF' : 'transparent',
              color: '#475569',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="4" y1="7"  x2="20" y2="7"/>
              <line x1="4" y1="12" x2="20" y2="12"/>
              <line x1="4" y1="17" x2="20" y2="17"/>
            </svg>
          </button>
        );

        const openMenuBrand = openRowMenuId ? displayed.find(r => r.id === openRowMenuId) : null;
        const renderMenuPortal = () => {
          if (!openMenuBrand || !openRowMenuPos) return null;
          const r = openMenuBrand;
          const bCount = branchCountMap[r.id] || 0;
          return createPortal(
            <div
              className="brn-row-menu"
              style={{
                position: 'fixed',
                top:  openRowMenuPos.top,
                left: openRowMenuPos.left,
                width: MENU_W,
                background: '#fff',
                border: '1px solid #E5E7EB', borderRadius: 10,
                boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                zIndex: 100000, overflow: 'hidden',
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Header: brand name (non-clickable label). */}
              <div className="ap-row-menu-header" title={r.name_ar || r.name}>
                {r.logo_path
                  ? <img className="ap-row-menu-header__logo" src={r.logo_path} alt="" />
                  : <span className="ap-row-menu-header__dot" style={{ background: '#42A5F5' }} />}
                <span className="ap-row-menu-header__name">{r.name}</span>
              </div>
              <button type="button" className="ap-row-menu-item"
                onClick={() => { closeRowMenu(); navigate(`/brands/${r.id}/menu`); }}>
                📋 Open Menu Editor
              </button>
              <button type="button" className="ap-row-menu-item"
                onClick={() => { closeRowMenu(); localStorage.setItem('pm_last_brand', String(r.id)); navigate(`/brands/${r.id}/branches`); }}>
                📍 View branches ({bCount})
              </button>
              <button type="button" className="ap-row-menu-item"
                onClick={() => { closeRowMenu(); navigate(`/brands/${r.id}/slides`); }}>
                🎨 Open Slides
              </button>
              <button type="button" className="ap-row-menu-item"
                onClick={() => { closeRowMenu(); navigate(`/brands/${r.id}/info-screens`); }}>
                🪧 Info Screens
              </button>
              <button type="button" className="ap-row-menu-item"
                onClick={() => { closeRowMenu(); navigate(`/brands/${r.id}/qr`); }}>
                📱 QR Code
              </button>
              {isAdmin && <>
                <div style={{ height: 1, background: '#F1F5F9', margin: '2px 0' }} />
                <button type="button" className="ap-row-menu-item"
                  onClick={() => { closeRowMenu(); openEdit(r); }}>
                  ✏️ Edit brand
                </button>
                <button type="button" className="ap-row-menu-item"
                  onClick={() => { closeRowMenu(); handleDuplicate(r.id); }}>
                  📄 Duplicate brand
                </button>
                <button type="button" className="ap-row-menu-item"
                  onClick={() => { closeRowMenu(); handleDelete(r.id); }}
                  style={{ color: '#B91C1C' }}>
                  🗑 Delete brand
                </button>
              </>}
            </div>,
            document.body
          );
        };

        // ── Table renderer ─────────────────────────────────────────
        const renderTableRow = (r) => {
          const bCount = branchCountMap[r.id] || 0;
          const hasMenu = hasMenuFn(r);
          const caps = Array.isArray(r.capabilities) ? r.capabilities : [];
          return (
            <tr key={r.id}
              className="ap-row brn-row"
              title="Click for menu · double-click to open menu editor"
              onClick={(e) => {
                if (e.detail >= 2) return;
                openMenuAt(r, e.clientX, e.clientY);
              }}
              onDoubleClick={() => { closeRowMenu(); navigate(`/brands/${r.id}/menu`); }}
            >
              {/* Brand cell: logo + name + arabic */}
              <td className="ap-td">
                <div className="ap-user-cell">
                  {r.logo_path
                    ? <img className="ap-avatar" src={r.logo_path} alt={r.name}
                        style={{ background: '#F7F7F7', border: '1px solid #EBEBEB', padding: 2, objectFit: 'contain' }} />
                    : <div className="ap-avatar" style={{
                        background: 'linear-gradient(135deg,#E3F2FD,#BBDEFB)',
                        color: '#1565C0',
                      }}>{r.name?.charAt(0).toUpperCase()}</div>}
                  <div className="ap-user-name">
                    <div className="ap-user-line1"><span className="ap-username">{r.name}</span></div>
                    {r.name_ar && <div className="ap-user-line2" style={{ fontFamily: 'Cairo' }}>{r.name_ar}</div>}
                  </div>
                </div>
              </td>
              {/* Owner (admin only) */}
              {isAdmin && (
                <td className="ap-td">
                  {r.owner_username
                    ? <span className="ap-parent">👤 {r.owner_username}</span>
                    : <span style={{ color: '#CBD5E1' }}>—</span>}
                </td>
              )}
              {/* Menu status */}
              <td className="ap-td">
                {hasMenu
                  ? <span className="ap-role-badge" style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FCA5A5' }}>● HAS MENU</span>
                  : <span className="ap-role-badge" style={{ background: '#F1F5F9', color: '#94A3B8', border: '1px solid #E2E8F0' }}>○ NO MENU</span>}
              </td>
              {/* Branches count */}
              <td className="ap-td ap-td--counts">
                <button type="button" className="ap-count" title="View branches"
                  onClick={(e) => { e.stopPropagation(); closeRowMenu(); localStorage.setItem('pm_last_brand', String(r.id)); navigate(`/brands/${r.id}/branches`); }}>
                  <span style={{ color: '#16A34A' }}>📍</span>
                  <span className="ap-count-num">{bCount}</span>
                </button>
              </td>
              {/* Capabilities */}
              <td className="ap-td">
                <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                  {caps.includes('signage') && <span className="ap-role-badge" style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>SIG</span>}
                  {caps.includes('qr_menu') && <span className="ap-role-badge" style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }}>QR</span>}
                  {caps.includes('pos')      && <span className="ap-role-badge" style={{ background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' }}>POS</span>}
                  {caps.length === 0 && <span style={{ color: '#CBD5E1' }}>—</span>}
                </div>
              </td>
              {/* Kebab cell removed — row click opens the menu directly. */}
            </tr>
          );
        };

        // ── Empty state already handled above ──────────────────────
        if (displayed.length === 0) return null;

        if (viewMode === 'table') {
          return (
            <>
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr>
                      <th>Brand</th>
                      {isAdmin && <th>Owner</th>}
                      <th>Menu</th>
                      <th>Branches</th>
                      <th>Capabilities</th>
                    </tr>
                  </thead>
                  <tbody>{displayed.map(renderTableRow)}</tbody>
                </table>
              </div>
              {renderMenuPortal()}
            </>
          );
        }

        // Cards grid (unchanged behaviour)
        return (
          <>
            <div className="grid-3">
              {displayed.map(r => (
                <BrandCard
                  key={r.id}
                  r={r}
                  bCount={branchCountMap[r.id] || 0}
                  hasMenu={hasMenuFn(r)}
                  isAdmin={isAdmin}
                  onMenu={e => { e.stopPropagation(); navigate(`/brands/${r.id}/menu`); }}
                  onQR={e => { e.stopPropagation(); navigate(`/brands/${r.id}/qr`); }}
                  onSlides={e => { e.stopPropagation(); navigate(`/brands/${r.id}/slides`); }}
                  onBranches={e => { e.stopPropagation(); localStorage.setItem('pm_last_brand', String(r.id)); navigate(`/brands/${r.id}/branches`); }}
                  onEdit={e => { e.stopPropagation(); openEdit(r); }}
                  onDuplicate={e => { e.stopPropagation(); handleDuplicate(r.id); }}
                  onDelete={e => { e.stopPropagation(); handleDelete(r.id); }}
                />
              ))}
            </div>
            {renderMenuPortal()}
          </>
        );
      })()}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Brand' : 'Add Brand'}</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Row 1: Owner | Logo */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                {user?.role === 'admin' && (
                  <div className="input-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                    <label>Owner</label>
                    <select className="input" value={form.owner_id} onChange={e => setForm({ ...form, owner_id: e.target.value })} required style={{ height: 60, boxSizing: 'border-box' }}>
                      <option value="">— Select Owner —</option>
                      {owners.map(o => <option key={o.id} value={o.id}>{o.username} ({o.role})</option>)}
                    </select>
                  </div>
                )}
                <div className="input-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                  <label>Brand Logo</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 60 }}>
                    <div style={{ width: 60, height: 60, borderRadius: 12, background: '#f5f5f5', border: '1.5px dashed #E0E0E0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                      {pendingLogoPreview
                        ? <img src={pendingLogoPreview} alt="logo preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        : form.logo_path
                          ? <img src={form.logo_path} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          : <span style={{ fontSize: 26, color: '#ccc' }}>🏪</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleLogoUpload} className="input" style={{ padding: 6, fontSize: 12 }} />
                      {pendingLogoFile && !logoUploading && <div style={{ fontSize: 11, color: '#16A34A', marginTop: 2 }}>Ready — saves on submit</div>}
                      {logoUploading && <div style={{ fontSize: 11, color: '#2196F3', marginTop: 2 }}>Saving…</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Name EN | Name AR */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <div className="input-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                  <label>Name (English)</label>
                  <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Cairo Grill" autoFocus />
                </div>
                <div className="input-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                  <label>Name (Arabic)</label>
                  <input className="input arabic" value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} placeholder="مثال: مطعم القاهرة" />
                </div>
              </div>
              {user?.role === 'admin' && (
                <div className="input-group">
                  <label>Capabilities</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[
                      { id: 'signage', label: 'Signage', hint: 'TV slides + menu boards' },
                      { id: 'qr_menu', label: 'QR Menu', hint: 'Public-facing QR menu page' },
                      { id: 'pos',     label: 'POS',     hint: 'Point-of-sale (coming soon)' },
                    ].map(cap => {
                      const checked = form.capabilities.includes(cap.id);
                      return (
                        <label
                          key={cap.id}
                          title={cap.hint}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 10px', borderRadius: 8,
                            border: `1.5px solid ${checked ? '#42A5F5' : '#E0E0E0'}`,
                            background: checked ? '#E3F2FD' : '#FAFAFA',
                            cursor: 'pointer',
                            fontSize: 13, fontWeight: 600, color: checked ? '#1565C0' : '#555',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm(f => {
                                const has = f.capabilities.includes(cap.id);
                                const next = has
                                  ? f.capabilities.filter(c => c !== cap.id)
                                  : [...f.capabilities, cap.id];
                                return { ...f, capabilities: next };
                              });
                            }}
                          />
                          {cap.label}
                        </label>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: form.capabilities.length === 0 ? '#B91C1C' : '#888', marginTop: 4 }}>
                    {form.capabilities.length === 0
                      ? 'No capabilities enabled — owner cannot use this brand until at least one is granted.'
                      : 'Toggle the features this brand should be able to use.'}
                  </div>
                </div>
              )}
              {editing && (
                <div className="input-group">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <label style={{ margin: 0 }}>Assigned Managers</label>
                    {user?.role === 'admin' && (
                      <button
                        type="button"
                        onClick={() => {
                          // Cross-app handoff: tell main signage app to open AdminPanel
                          // → User Manager, with the Add User modal pre-filled for a
                          // brand_manager under this brand's owner.
                          sessionStorage.setItem('pm_user_modal_preset', JSON.stringify({
                            mode: 'add',
                            role: 'brand_manager',
                            parent_user_id: editing.user_id,
                            brand_id: editing.id,
                          }));
                          setShowModal(false);
                          window.dispatchEvent(new CustomEvent('pm:open-user-manager'));
                        }}
                        style={{
                          fontSize: 11, fontWeight: 700, color: '#1D4ED8',
                          background: '#EFF6FF', border: '1.5px solid #BFDBFE',
                          borderRadius: 6, padding: '3px 10px', cursor: 'pointer',
                        }}
                      >+ Add Manager</button>
                    )}
                  </div>
                  {assignedManagers.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#888', padding: '6px 0' }}>
                      No managers or operators assigned to this brand yet.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {assignedManagers.map(m => {
                        const roleColor = {
                          brand_manager:  { bg: '#EEF2FF', text: '#3730A3', label: 'Brand Mgr' },
                          branch_manager: { bg: '#ECFEFF', text: '#155E75', label: 'Branch Mgr' },
                          operator:       { bg: '#F0FDF4', text: '#166534', label: 'Operator' },
                        }[m.role] || { bg: '#F1F5F9', text: '#475569', label: m.role };
                        const clickable = user?.role === 'admin';
                        return (
                          <div
                            key={m.id}
                            onClick={() => {
                              if (!clickable) return;
                              sessionStorage.setItem('pm_user_modal_preset', JSON.stringify({
                                mode: 'edit', username: m.username, user_id: m.id,
                              }));
                              setShowModal(false);
                              window.dispatchEvent(new CustomEvent('pm:open-user-manager'));
                            }}
                            title={clickable ? `Edit ${m.username}` : ''}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 10px', background: '#FAFAFA',
                              border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13,
                              cursor: clickable ? 'pointer' : 'default',
                              transition: 'background 0.15s',
                            }}
                            onMouseOver={e => { if (clickable) e.currentTarget.style.background = '#EFF6FF'; }}
                            onMouseOut={e  => { if (clickable) e.currentTarget.style.background = '#FAFAFA'; }}
                          >
                            <span style={{ fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.username}
                            </span>
                            <span style={{
                              background: roleColor.bg, color: roleColor.text,
                              padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            }}>{roleColor.label}</span>
                            <span style={{ fontSize: 11, color: '#666' }}>
                              {(m.scopes || []).join(' · ')}
                              {m.can_edit ? '' : ' · read-only'}
                              {m.can_delete ? ' · can delete' : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
                {editing ? 'Save Changes' : 'Create Brand'}
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Scroll-to-top is now global (in Sidebar.js) — listens to .sb-main scroll. */}
    </div>
  );
}
